/**
 * LayoutPublic — même header/footer que Layout, sans auth requise.
 * Utilisé pour les pages statiques (CGU, mentions légales, contact…)
 * accessibles aux visiteurs non connectés.
 */
import React, { useState } from "react";
import { NavLink, Link, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import LanguageSwitcher from "./LanguageSwitcher";

const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || "")
  .split(",").map(e => e.trim().toLowerCase()).filter(Boolean);

function isAdmin(user) {
  return !!(user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase()));
}

const Icon = {
  feed:      <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm0 8a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2z"/></svg>,
  directory: <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM2 8a2 2 0 114 0 2 2 0 01-4 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906zM10 12a5 5 0 015 5v1H5v-1a5 5 0 015-5z"/></svg>,
  encounters:<svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd"/></svg>,
  places:    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/></svg>,
  messages:  <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/><path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"/></svg>,
  profile:   <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/></svg>,
  logout:    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1H3zm7 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-1 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/></svg>,
};

export default function LayoutPublic() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const admin = isAdmin(user);

  const MAIN_NAV = [
    { to: "/", label: t("nav_feed") || "Fil", icon: Icon.feed, end: true },
    { to: "/directory", label: t("nav_directory") || "Annuaire", icon: Icon.directory },
    { to: "/encounters", label: t("nav_encounters") || "Rencontres", icon: Icon.encounters },
    { to: "/places", label: t("nav_places") || "Lieux", icon: Icon.places },
    { to: "/messages", label: t("nav_messages") || "Messages", icon: Icon.messages },
  ];

  return (
    <div className="min-h-screen bg-[#0e0e18]">

      {/* ── HEADER identique à Layout ── */}
      <header className="sticky top-0 z-40 bg-[#0e0e18]/95 backdrop-blur-xl"
        style={{ borderBottom: "1px solid rgba(139,111,245,0.12)" }}>
        <div className="mx-auto flex h-16 max-w-screen-xl items-center px-5 sm:px-8">

          <NavLink to="/" className="mr-8 shrink-0">
            <img src="/logo.png" alt="Univers Libertin" className="h-10 w-auto" />
          </NavLink>

          {/* Nav principale — visible si connecté, sinon liens publics */}
          <nav className="hidden flex-1 items-center gap-1 sm:flex">
            {user ? (
              MAIN_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition-all duration-150 ${
                      isActive
                        ? "bg-[#8b6ff5]/15 text-[#c4b5fd]"
                        : "text-white/40 hover:bg-white/[0.04] hover:text-white/75"
                    }`
                  }
                >
                  {item.icon}
                  <span>{item.label}</span>
                </NavLink>
              ))
            ) : (
              /* Visiteur non connecté */
              <div className="flex items-center gap-1">
                {[
                  ["/p/comment-ca-marche", "Comment ça marche"],
                  ["/p/tarifs", "Tarifs"],
                  ["/p/a-propos", "À propos"],
                ].map(([to, label]) => (
                  <NavLink key={to} to={to}
                    className={({ isActive }) =>
                      `rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${
                        isActive ? "text-[#c4b5fd]" : "text-white/40 hover:text-white/75"
                      }`
                    }>
                    {label}
                  </NavLink>
                ))}
              </div>
            )}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <LanguageSwitcher compact />

            {user ? (
              /* Avatar dropdown */
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-white transition-all"
                  style={{
                    background: "linear-gradient(135deg,#7c3aed,#5b21b6)",
                    boxShadow: dropdownOpen ? "0 0 0 2px #8b6ff5" : "0 0 0 2px rgba(139,111,245,0.2)",
                  }}
                >
                  {(user?.username || user?.email || "?")[0].toUpperCase()}
                </button>
                {dropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)} />
                    <div className="absolute right-0 top-11 z-50 w-52 overflow-hidden rounded-xl py-1 shadow-2xl"
                      style={{ background: "#1a1a2e", border: "1px solid rgba(139,111,245,0.18)" }}>
                      <div className="px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                        <p className="text-[13px] font-semibold text-white/80">{user?.username || "Mon profil"}</p>
                        <p className="truncate text-[11px] text-white/30">{user?.email}</p>
                      </div>
                      {[
                        { to: "/profile", label: "Profil", icon: Icon.profile },
                        ...(admin ? [{ to: "/admin", label: "Admin", icon: null, isAdmin: true }] : []),
                      ].map((item) => (
                        <NavLink key={item.to} to={item.to} onClick={() => setDropdownOpen(false)}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium transition-colors ${
                              item.isAdmin ? "text-amber-400/70 hover:text-amber-300 hover:bg-amber-500/8"
                              : isActive ? "bg-[#8b6ff5]/10 text-[#c4b5fd]"
                              : "text-white/50 hover:bg-white/[0.04] hover:text-white/85"
                            }`
                          }>
                          <span className="text-white/30">{item.icon}</span>
                          {item.label}
                        </NavLink>
                      ))}
                      <div style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                        <button onClick={() => { setDropdownOpen(false); logout(); }}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-rose-400/60 transition-colors hover:bg-rose-500/8 hover:text-rose-400">
                          {Icon.logout}
                          {t("nav_logout") || "Déconnexion"}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* Bouton connexion pour visiteur */
              <NavLink to="/welcome"
                className="rounded-lg px-4 py-2 text-sm font-semibold text-white transition-all"
                style={{ background: "linear-gradient(135deg,#7c3aed,#5b21b6)" }}>
                Connexion
              </NavLink>
            )}
          </div>
        </div>
      </header>

      {/* ── CONTENU ── */}
      <main className="mx-auto w-full max-w-screen-xl px-5 py-6 sm:px-8">
        <Outlet />
      </main>

      {/* ── FOOTER identique à Layout ── */}
      <footer className="hidden sm:block" style={{ background: "#09090f", borderTop: "1px solid rgba(139,111,245,0.1)" }}>
        <div className="mx-auto max-w-screen-xl px-8 py-12">
          <div className="grid grid-cols-4 gap-8">

            <div>
              <Link to="/" className="mb-4 block">
                <img src="/logo.png" alt="Univers Libertin" className="h-12 w-auto" />
              </Link>
              <p className="text-[12px] leading-relaxed" style={{ color: "rgba(255,255,255,0.35)" }}>
                La communauté libertine de référence. Rencontres, lieux et événements en toute discrétion.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {["🔒 Modéré", "🛡 Discret", "💳 Stripe"].map(b => (
                  <span key={b} className="rounded-full px-2.5 py-1 text-[10px]"
                    style={{ background: "rgba(139,111,245,0.08)", color: "rgba(196,181,253,0.6)", border: "1px solid rgba(139,111,245,0.15)" }}>
                    {b}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h4 className="mb-4 text-[11px] font-semibold uppercase tracking-widest" style={{ color: "#8b6ff5" }}>Découvrir</h4>
              {[["/p/a-propos","À propos"],["/p/comment-ca-marche","Comment ça marche"],["/p/annuaire-clubs-libertins-france","Annuaire des clubs"],["/p/rencontres-libertines","Rencontres"],["/p/tarifs","Tarifs"]].map(([to,label]) => (
                <Link key={to} to={to} className="mb-2.5 block text-[13px] transition-colors hover:text-white/80" style={{ color: "rgba(255,255,255,0.45)" }}>{label}</Link>
              ))}
            </div>

            <div>
              <h4 className="mb-4 text-[11px] font-semibold uppercase tracking-widest" style={{ color: "#8b6ff5" }}>Légal</h4>
              {[["/p/mentions-legales","Mentions légales"],["/p/politique-de-confidentialite","Confidentialité"],["/p/conditions-generales-utilisation","CGU"],["/p/charte-communautaire","Charte communautaire"]].map(([to,label]) => (
                <Link key={to} to={to} className="mb-2.5 block text-[13px] transition-colors hover:text-white/80" style={{ color: "rgba(255,255,255,0.45)" }}>{label}</Link>
              ))}
            </div>

            <div>
              <h4 className="mb-4 text-[11px] font-semibold uppercase tracking-widest" style={{ color: "#8b6ff5" }}>Contact</h4>
              {[["/p/contact","Nous contacter"],["/p/securite-confidentialite","Sécurité"]].map(([to,label]) => (
                <Link key={to} to={to} className="mb-2.5 block text-[13px] transition-colors hover:text-white/80" style={{ color: "rgba(255,255,255,0.45)" }}>{label}</Link>
              ))}
              <a href="mailto:contact@universlibertin.fr" className="mt-3 block text-[12px] transition-colors hover:text-[#c4b5fd]" style={{ color: "rgba(139,111,245,0.6)" }}>
                contact@universlibertin.fr
              </a>
            </div>
          </div>

          <div className="mt-10 flex items-center justify-between pt-6" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
            <p className="text-[11px]" style={{ color: "rgba(255,255,255,0.2)" }}>© {new Date().getFullYear()} Univers Libertin — TRUSTOVA SAS — Tous droits réservés</p>
            <p className="text-[11px]" style={{ color: "rgba(255,255,255,0.15)" }}>Réservé aux adultes · +18</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
